import { Controller } from "@hotwired/stimulus"

interface RobotData {
  id: number
  name: string
  image_url: string
}

interface Card {
  index: number
  robotId: number
  name: string
  imageUrl: string
  isFlipped: boolean
  isMatched: boolean
}

// Shared AudioContext for sounds
let audioCtx: AudioContext | null = null
function getAudioContext(): AudioContext {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
  }
  if (audioCtx.state === "suspended") {
    audioCtx.resume()
  }
  return audioCtx
}

function trackEvent(eventName: string, params?: Record<string, string | number>): void {
  if (typeof window !== "undefined" && (window as any).gtag) {
    (window as any).gtag("event", eventName, params)
  }
}

function escapeHtml(str: string): string {
  const div = document.createElement("div")
  div.textContent = str
  return div.innerHTML
}

export default class extends Controller<HTMLElement> {
  static values = {
    gridSize: Number,
    robots: Array,
    submitUrl: String
  }

  static targets = ["board", "boardContainer", "timer", "moves", "matches", "totalPairs", "completion",
                     "completionTime", "completionMoves", "completionStars"]

  declare readonly gridSizeValue: number
  declare readonly robotsValue: RobotData[]
  declare readonly submitUrlValue: string
  declare readonly boardTarget: HTMLElement
  declare readonly boardContainerTarget: HTMLElement
  declare readonly timerTarget: HTMLElement
  declare readonly movesTarget: HTMLElement
  declare readonly matchesTarget: HTMLElement
  declare readonly totalPairsTarget: HTMLElement
  declare readonly completionTarget: HTMLElement
  declare readonly completionTimeTarget: HTMLElement
  declare readonly completionMovesTarget: HTMLElement
  declare readonly completionStarsTarget: HTMLElement

  cards: Card[] = []
  flippedCards: number[] = []
  matchedCount: number = 0
  totalPairs: number = 0
  moveCount: number = 0
  timerInterval: ReturnType<typeof setInterval> | null = null
  seconds: number = 0
  isLocked: boolean = false
  gameStarted: boolean = false

  connect(): void {
    this.totalPairs = (this.gridSizeValue * this.gridSizeValue) / 2
    // Defer to ensure layout is computed
    requestAnimationFrame(() => this.initGame())
  }

  disconnect(): void {
    if (this.timerInterval) clearInterval(this.timerInterval)
  }

  initGame(): void {
    this.cards = []
    this.flippedCards = []
    this.matchedCount = 0
    this.moveCount = 0
    this.seconds = 0
    this.isLocked = false
    this.gameStarted = false
    if (this.timerInterval) clearInterval(this.timerInterval)
    this.timerTarget.textContent = "00:00"

    const robots = this.robotsValue.slice(0, this.totalPairs)
    const cardPairs: Card[] = []

    robots.forEach((robot) => {
      for (let j = 0; j < 2; j++) {
        cardPairs.push({
          index: 0,
          robotId: robot.id,
          name: robot.name,
          imageUrl: robot.image_url,
          isFlipped: false,
          isMatched: false
        })
      }
    })

    this.cards = this.shuffle(cardPairs).map((card, i) => ({ ...card, index: i }))
    this.renderBoard()
    this.updateUI()
  }

  renderBoard(): void {
    const grid = this.gridSizeValue
    const board = this.boardTarget
    board.replaceChildren()

    // Calculate board dimensions to fill container with card-shaped (3:4) cells
    const container = this.boardContainerTarget
    const containerStyle = getComputedStyle(container)
    const availW = container.clientWidth - parseFloat(containerStyle.paddingLeft) - parseFloat(containerStyle.paddingRight)
    const availH = container.clientHeight - parseFloat(containerStyle.paddingTop) - parseFloat(containerStyle.paddingBottom) - 12
    const gapSize = grid <= 4 ? 8 : grid <= 6 ? 6 : 4
    // Square cards: fit the largest square cells into available space
    const maxCardH = (availH - (grid - 1) * gapSize) / grid
    const maxCardW = (availW - (grid - 1) * gapSize) / grid
    const cardSize = Math.min(maxCardH, maxCardW)
    const cardW = cardSize
    const cardH = cardSize

    const boardW = cardW * grid + gapSize * (grid - 1)
    const boardH = cardH * grid + gapSize * (grid - 1)

    board.style.width = `${boardW}px`
    board.style.height = `${boardH}px`
    board.style.gridTemplateColumns = `repeat(${grid}, ${cardW}px)`
    board.style.gridTemplateRows = `repeat(${grid}, ${cardH}px)`
    board.style.gap = `${gapSize}px`
    board.className = "grid"

    const sizeClass = grid <= 4 ? "rounded-xl" : grid <= 6 ? "rounded-lg" : "rounded-md"

    this.cards.forEach((card) => {
      const cardEl = document.createElement("div")
      cardEl.dataset.cardIndex = String(card.index)
      cardEl.className = "memory-card cursor-pointer perspective-500 overflow-hidden"
      cardEl.addEventListener("click", () => this.flipCard(card.index))

      // Build card inner structure with safe DOM methods
      const inner = document.createElement("div")
      inner.className = `memory-card-inner relative w-full h-full transition-transform duration-500 transform-style-3d ${sizeClass}`

      // Front face (question mark)
      const front = document.createElement("div")
      front.className = `memory-card-front absolute inset-0 backface-hidden bg-gradient-to-br from-indigo-500 to-purple-600 ${sizeClass} flex items-center justify-center shadow-lg border border-white/20`
      const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      svg.setAttribute("class", "w-1/3 h-1/3 text-white/60")
      svg.setAttribute("fill", "none")
      svg.setAttribute("stroke", "currentColor")
      svg.setAttribute("viewBox", "0 0 24 24")
      const path = document.createElementNS("http://www.w3.org/2000/svg", "path")
      path.setAttribute("stroke-linecap", "round")
      path.setAttribute("stroke-linejoin", "round")
      path.setAttribute("stroke-width", "2")
      path.setAttribute("d", "M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z")
      svg.appendChild(path)
      front.appendChild(svg)

      // Back face (image)
      const back = document.createElement("div")
      back.className = `memory-card-back absolute inset-0 backface-hidden rotate-y-180 bg-white ${sizeClass} overflow-hidden shadow-lg border-2 border-transparent`
      const img = document.createElement("img")
      img.src = card.imageUrl
      img.alt = escapeHtml(card.name)
      img.className = "w-full h-full object-cover"
      img.loading = "eager"
      back.appendChild(img)

      // Name label for larger grids
      if (grid <= 6) {
        const label = document.createElement("div")
        label.className = "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-1 sm:p-1.5"
        const span = document.createElement("span")
        span.className = "text-white text-[10px] sm:text-xs font-bold truncate block text-center"
        span.textContent = card.name
        label.appendChild(span)
        back.appendChild(label)
      }

      inner.appendChild(front)
      inner.appendChild(back)
      cardEl.appendChild(inner)
      board.appendChild(cardEl)
    })
  }

  flipCard(index: number): void {
    if (this.isLocked) return
    const card = this.cards[index]
    if (card.isFlipped || card.isMatched) return
    if (this.flippedCards.length >= 2) return

    if (!this.gameStarted) {
      this.gameStarted = true
      this.startTimer()
      trackEvent("memory_game_start", { grid_size: this.gridSizeValue, total_pairs: this.totalPairs })
    }

    card.isFlipped = true
    this.flippedCards.push(index)
    this.animateFlip(index, true)
    this.playSound("flip")

    if (this.flippedCards.length === 2) {
      this.moveCount++
      this.updateUI()
      this.checkMatch()
    }
  }

  checkMatch(): void {
    this.isLocked = true
    const [i1, i2] = this.flippedCards
    const card1 = this.cards[i1]
    const card2 = this.cards[i2]

    if (card1.robotId === card2.robotId) {
      card1.isMatched = true
      card2.isMatched = true
      this.matchedCount++
      this.updateUI()

      setTimeout(() => {
        this.animateMatch(i1)
        this.animateMatch(i2)
        this.playSound("match")
        this.flippedCards = []
        this.isLocked = false

        if (this.matchedCount === this.totalPairs) {
          setTimeout(() => this.gameComplete(), 600)
        }
      }, 300)
    } else {
      this.playSound("miss")
      setTimeout(() => {
        card1.isFlipped = false
        card2.isFlipped = false
        this.animateFlip(i1, false)
        this.animateFlip(i2, false)
        this.flippedCards = []
        this.isLocked = false
      }, 800)
    }
  }

  animateFlip(index: number, show: boolean): void {
    const cardEl = this.boardTarget.querySelector(`[data-card-index="${index}"]`)
    if (!cardEl) return
    const inner = cardEl.querySelector(".memory-card-inner") as HTMLElement
    if (show) {
      inner.classList.add("rotate-y-180")
    } else {
      inner.classList.remove("rotate-y-180")
    }
  }

  animateMatch(index: number): void {
    const cardEl = this.boardTarget.querySelector(`[data-card-index="${index}"]`)
    if (!cardEl) return
    const inner = cardEl.querySelector(".memory-card-inner") as HTMLElement
    const back = cardEl.querySelector(".memory-card-back") as HTMLElement
    if (back) back.classList.add("border-emerald-400")
    inner.classList.add("scale-95")
    setTimeout(() => inner.classList.remove("scale-95"), 200)
  }

  startTimer(): void {
    this.timerInterval = setInterval(() => {
      this.seconds++
      this.timerTarget.textContent = this.formatTime(this.seconds)
    }, 1000)
  }

  formatTime(s: number): string {
    const min = Math.floor(s / 60)
    const sec = s % 60
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`
  }

  updateUI(): void {
    this.movesTarget.textContent = String(this.moveCount)
    this.matchesTarget.textContent = String(this.matchedCount)
    this.totalPairsTarget.textContent = String(this.totalPairs)
  }

  gameComplete(): void {
    if (this.timerInterval) clearInterval(this.timerInterval)
    this.playSound("complete")

    this.completionTimeTarget.textContent = this.formatTime(this.seconds)
    this.completionMovesTarget.textContent = String(this.moveCount)
    trackEvent("memory_game_complete", {
      grid_size: this.gridSizeValue,
      total_pairs: this.totalPairs,
      moves: this.moveCount,
      time_seconds: this.seconds
    })

    this.submitResult()

    // Stars based on efficiency (moves / pairs ratio)
    const ratio = this.moveCount / this.totalPairs
    let stars = 1
    if (ratio <= 1.5) stars = 3
    else if (ratio <= 2.2) stars = 2

    const container = this.completionStarsTarget
    container.replaceChildren()
    for (let i = 0; i < 3; i++) {
      const starSvg = document.createElementNS("http://www.w3.org/2000/svg", "svg")
      starSvg.setAttribute("class", `w-10 h-10 sm:w-12 sm:h-12 ${i < stars ? "text-yellow-400" : "text-slate-600"} transition-all duration-300`)
      starSvg.setAttribute("style", `animation-delay: ${i * 0.15}s`)
      starSvg.setAttribute("fill", "currentColor")
      starSvg.setAttribute("viewBox", "0 0 20 20")
      const starPath = document.createElementNS("http://www.w3.org/2000/svg", "path")
      starPath.setAttribute("d", "M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z")
      starSvg.appendChild(starPath)
      container.appendChild(starSvg)
    }

    this.completionTarget.classList.remove("hidden")
    this.completionTarget.classList.add("flex")
  }

  submitResult(): void {
    if (!this.submitUrlValue) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    if (!csrfToken) return

    fetch(this.submitUrlValue, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken
      },
      body: JSON.stringify({
        grid_size: this.gridSizeValue,
        moves: this.moveCount,
        time_seconds: this.seconds
      })
    }).catch(() => {
      // Silently fail - game result is still shown
    })
  }

  restart(): void {
    this.completionTarget.classList.add("hidden")
    this.completionTarget.classList.remove("flex")
    this.initGame()
  }

  shuffle<T>(array: T[]): T[] {
    const arr = [...array]
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]]
    }
    return arr
  }

  playSound(type: "flip" | "match" | "miss" | "complete"): void {
    try {
      const ctx = getAudioContext()
      switch (type) {
        case "flip": {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.type = "sine"
          osc.frequency.setValueAtTime(500, ctx.currentTime)
          osc.frequency.exponentialRampToValueAtTime(800, ctx.currentTime + 0.05)
          gain.gain.setValueAtTime(0.1, ctx.currentTime)
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.1)
          osc.start()
          osc.stop(ctx.currentTime + 0.1)
          break
        }
        case "match": {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.type = "sine"
          osc.frequency.setValueAtTime(660, ctx.currentTime)
          osc.frequency.exponentialRampToValueAtTime(880, ctx.currentTime + 0.15)
          gain.gain.setValueAtTime(0.2, ctx.currentTime)
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4)
          osc.start()
          osc.stop(ctx.currentTime + 0.4)
          break
        }
        case "miss": {
          const osc = ctx.createOscillator()
          const gain = ctx.createGain()
          osc.connect(gain)
          gain.connect(ctx.destination)
          osc.type = "triangle"
          osc.frequency.setValueAtTime(200, ctx.currentTime)
          osc.frequency.linearRampToValueAtTime(150, ctx.currentTime + 0.2)
          gain.gain.setValueAtTime(0.15, ctx.currentTime)
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.2)
          osc.start()
          osc.stop(ctx.currentTime + 0.2)
          break
        }
        case "complete": {
          const now = ctx.currentTime
          const notes = [523.25, 659.25, 783.99, 1046.50]
          notes.forEach((freq, i) => {
            const osc = ctx.createOscillator()
            const gain = ctx.createGain()
            osc.connect(gain)
            gain.connect(ctx.destination)
            osc.type = "sine"
            osc.frequency.value = freq
            const time = now + i * 0.12
            gain.gain.setValueAtTime(0.2, time)
            gain.gain.exponentialRampToValueAtTime(0.01, time + 0.4)
            osc.start(time)
            osc.stop(time + 0.4)
          })
          break
        }
      }
    } catch (_e) {
      // Audio not supported
    }
  }
}
